import { DropdownField } from 'ui-components/components/dropdown/dropdown-field';

/**
 * @typedef {import('./dropdown-types.d.ts').DropdownOption} DropdownOption
 */
/**
 * @typedef {import('./dropdown-types.d.ts').DropdownOptionGroup} DropdownOptionGroup
 */
/**
 * @typedef {import('./dropdown-types.d.ts').DropdownInlineMessageType} DropdownInlineMessageType
 */
/**
 * @typedef {import('../../components/text/text-types.d.ts').TextValue} TextValue
 */

/**
 * @param {object} props
 * @param {string} [props.ariaLabel]
 * @param {(option: DropdownOption) => boolean} [props.getOptionIsDisabled] - Defaults to `option.disabled`
 * @param {(option: DropdownOption, selectedOptions: DropdownOption[]) => boolean} [props.getOptionIsSelected] - This is typically not necessary unless there are multiple values used to determine if an option is selected. This should be used in conjunction with `selectedOption` instead of `selectedItemValue`
 * @param {(option: DropdownOption) => string} [props.getOptionLabel] - Defaults to `option.label`
 * @param {(option: DropdownOption) => string} [props.getOptionValue] - Defaults to `option.value`
 * @param {string} props.id
 * @param {boolean} [props.isDisabled]
 * @param {DropdownOption[]} props.options
 * @param {string} [props.placeholder]
 * @param {TextValue} [props.label]
 * @param {(option: DropdownOption) => ReactNode} [props.renderOption]
 * @param {(option: DropdownOption) => ReactNode} [props.renderSelectedValue]
 * @param {string} [props.selectedItemValue]
 * @param {DropdownOption} [props.selectedOption] - `selectedItemValue` should typically be preferred over this. `selectedOption` is only necessary when the selected option is not in the list of options or when multiple properties are needed to determine if an option is selected
 * @param {(value: string) => void} props.onItemSelected
 * @param {boolean} [props.shouldTruncateGroupHeader]
 * @param {boolean} [props.isClearable]
 * @param {boolean} [props.required]
 * @param {boolean} [props.isRequiredError]
 * @param {React.ReactNode} [props.inlineMessage]
 * @param {DropdownInlineMessageType} [props.inlineMessageType]
 * @param {string} [props.className]
 * @param {boolean} [props.isMenuFullWidth]
 * @param {DropdownOptionGroup[]} [props.optionGroups]
 * @returns {JSX.Element}
 */
export const SearchableDropdownField = ({
  ariaLabel,
  className = '',
  isMenuFullWidth,
  getOptionIsDisabled,
  getOptionIsSelected,
  getOptionLabel,
  getOptionValue,
  id,
  isDisabled,
  options,
  optionGroups,
  placeholder,
  label,
  isRequiredError = false,
  required = false,
  renderOption,
  renderSelectedValue,
  selectedItemValue = '',
  selectedOption,
  shouldTruncateGroupHeader,
  onItemSelected,
  isClearable,
  inlineMessage,
  inlineMessageType,
}) => {
  return (
    <DropdownField
      ariaLabel={ariaLabel}
      className={className}
      isMenuFullWidth={isMenuFullWidth}
      getOptionIsDisabled={getOptionIsDisabled}
      getOptionIsSelected={getOptionIsSelected}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      id={id}
      inlineMessage={inlineMessage}
      inlineMessageType={inlineMessageType}
      isClearable={isClearable}
      isDisabled={isDisabled}
      isRequiredError={isRequiredError}
      isSearchable
      label={label}
      onItemSelected={onItemSelected}
      optionGroups={optionGroups}
      options={options}
      placeholder={placeholder}
      renderOption={renderOption}
      renderSelectedValue={renderSelectedValue}
      required={required}
      selectedItemValue={selectedItemValue}
      selectedOption={selectedOption}
      shouldTruncateGroupHeader={shouldTruncateGroupHeader}
    />
  );
};
